export enum LiveReflectionChannel {
	ClientsReflection = 'clients_reflection',
	DealersReflection = 'dealers_reflection',
}

export enum LiveReflectionSocketEvent {
	Client = 'CLIENT',
}

export enum LiveReflectionUserType {
	Client = 1,
	Office = 2,
	Dealer = 3,
}

// Code-backed from servercore-api GenericConstant.ReflectionToken and RedisReflectionUtil.
export enum LiveReflectionToken {
	CreateClientOffice = 1,
	CreateDealer = 2,
	UpdateClientOffice = 3,
	UpdateDealer = 4,
	DeleteClientOffice = 5,
	DeleteDealer = 6,
	ClientLogin = 7,
	DealerLogin = 8,
	ClientLogout = 9,
	DealerLogout = 10,
	NewMoneyTransaction = 11,
	UpdateMoneyTransaction = 12,
	DeleteMoneyTransaction = 13,
	DisconnectClient = 14,
	DisconnectDealer = 15,
	DisconnectClientInside = 16,
	DisconnectDealerInside = 17,
	TransferClientOffice = 18,
	TransferDealer = 19,
	NewClientCurrencyPolicy = 20,
	UpdateClientCurrencyPolicy = 21,
	DeleteCurrencyPolicy = 22,
	NewClientGenericPolicy = 23,
	UpdateClientGenericPolicy = 24,
	DeleteClientGenericPolicy = 25,
	NewDealerCurrencyPolicy = 26,
	UpdateDealerCurrencyPolicy = 27,
	NewDealerGenericPolicy = 28,
	UpdateDealerGenericPolicy = 29,
	NewCurrency = 30,
	UpdateCurrency = 31,
	DeleteCurrency = 32,
	TransferCurrency = 33,
	AnnounceNote = 34,
	ManualOpenPosition = 35,
	UpdateOpenPosition = 36,
	DeleteOpenPosition = 37,
	ManualClosePosition = 38,
	UpdateClosePosition = 39,
	DeleteClosePosition = 40,
	NewMarket = 41,
	CloseMarket = 42,
	CloseMarketByHedge = 43,
	NewAmountUnit = 44,
	UpdateAmountUnit = 45,
	DeleteAmountUnit = 46,
	NewEntryOrder = 47,
	UpdateEntryOrder = 48,
	CancelEntryOrder = 49,
	NewSltpOrder = 50,
	UpdateSltpOrder = 51,
	CancelSltpOrder = 52,
	CopyCurrency = 53,
	NewRoboDealerPolicy = 54,
	UpdateRoboDealerPolicy = 55,
	DeleteRoboDealerPolicy = 56,
	EntryOrderHit = 57,
	SltpOrderHit = 58,
	UserLiquidated = 59,
	SendMail = 60,
	DeleteMail = 61,
	UpdateMail = 62,
	UpdatePublicSetting = 63,
	NewAccountMirroringPolicy = 64,
	UpdateAccountMirroringPolicy = 65,
	DeleteAccountMirroringPolicy = 66,
	NewAgentCommissionPolicy = 67,
	UpdateAgentCommissionPolicy = 68,
	DeleteAgentCommissionPolicy = 69,
	ResetFinancialStandingProgress = 70,
	ResetFinancialStandingCompleted = 71,
	HardDeleteUserProgress = 72,
	HardDeleteUserCompleted = 73,
	NewPriceAlert = 79,
	UpdatePriceAlert = 80,
	DeletePriceAlert = 81,
	PriceAlertHit = 82,
	NewBranch = 83,
	DeleteBranch = 84,
	UpdateBranch = 85,
	NewSoukItem = 86,
	DeleteSoukItem = 87,
	UpdateSoukItem = 88,
	CreateFrequentAlert = 89,
	DeleteFrequentAlert = 90,
	UpdateFrequentAlert = 91,
	ReachedMarginCall = 92,
	UnReachedMarginCall = 93,
	NewCashRequest = 94,
	UpdateCashRequest = 95,
	DeleteCashRequest = 96,
	CashDeliveryRequest = 97,
	NewCreditLoan = 98,
	UpdateCreditLoan = 99,
	DeleteCreditLoan = 100,
	AcceptCreditLoan = 101,
	RejectCreditLoan = 102,
	AcceptCreditLoanPartially = 103,
	PendingPositionRoboBeforeHandling = 104,
	PendingPositionRoboAfterHandling = 105,
	UpdatePendingPositionRoboAfterHandling = 106,
	SettlementProgress = 108,
	SettlementCompleted = 109,
	NewBank = 110,
	UpdateBank = 111,
	DeleteBank = 112,
	NewDepositRequest = 113,
	UpdateDepositRequest = 114,
	DeleteDepositRequest = 115,
	DepositRequest = 116,
	AgentCommissionProgress = 117,
	AgentCommissionCompleted = 118,
	FirstTimeDeposit = 119,
	NewWebhook = 120,
	UpdateWebhook = 121,
	DeleteWebhook = 122,
	FirstTimeWithdraw = 123,
	NewPlaylist = 124,
	UpdatePlaylist = 125,
	DeletePlaylist = 126,
	UpdateMessageProvider = 127,
	UpdatePaymentGateway = 128,
	NewLead = 129,
	UpdateLead = 130,
	DeleteLead = 131,
	NewLeadRemark = 132,
	UpdateLeadRemark = 133,
	DeleteLeadRemark = 134,
	NewLeadGroupAssignment = 135,
	UpdateLeadGroupAssignment = 136,
	DeleteLeadGroupAssignment = 137,
	NewLeadGroupAssignee = 138,
	UpdateLeadGroupAssignee = 139,
	DeleteLeadGroupAssignee = 140,
}

export interface LiveReflectionPayload<TData = unknown> {
	AFFECTED_ID?: number | string | null;
	FUNCTION_TOKEN?: number | string | null;
	data?: TData;
	[key: string]: unknown;
}

export interface LiveReflectionSocketPayload {
	message?: string | LiveReflectionPayload;
	event_name?: string;
	success?: boolean;
	[key: string]: unknown;
}

export interface LiveReflectionEvent<TData = unknown> {
	channel: string;
	token?: LiveReflectionToken | number;
	tokenName: string;
	affectedId?: number;
	data?: TData;
	payload?: LiveReflectionPayload<TData>;
	rawMessage: unknown;
	socketPayload: LiveReflectionSocketPayload;
	receivedAt: string;
}

export type LiveReflectionHandler = (event: LiveReflectionEvent) => void;

export const getLiveReflectionTokenName = (token?: number | string | null) => {
	if (token === undefined || token === null || token === '') {
		return 'Unknown';
	}

	const numericToken = Number(token);
	return LiveReflectionToken[numericToken] || `Unknown(${numericToken})`;
};

export const LIVE_REFLECTION_TEST_TOKENS = new Set<number>([
	LiveReflectionToken.NewMoneyTransaction,
	LiveReflectionToken.CreateClientOffice,
	LiveReflectionToken.CreateDealer,
	LiveReflectionToken.UpdateClientOffice,
	LiveReflectionToken.UpdateDealer,
]);
